/**
 * @fileoverview
 * sakanova API (`https://api.sakanova.f5.si`) の管理者認証と、認証付き fetch の集約層。
 *
 * 認証エコシステム:
 * - Access Token: RS256 JWT。`Authorization: Bearer` で送る。**メモリ上にのみ保持**し、
 *   localStorage / sessionStorage / Cookie へは保存しない。有効期間は応答の
 *   `expires_in` (秒) を正とする。
 * - Refresh Token: HttpOnly Cookie (`__Secure-sakanova_admin_refresh_token`)。JS からは
 *   読めず、`/v1/admin/auth/*` への fetch に `credentials: 'include'` を付けるだけで
 *   ブラウザが自動送信する。ローテーション制のため refresh は並行実行してはならない
 *   (シングルフライトで直列化する)。
 *
 * 管理 API は client-token / DPoP を使用しない。
 */

import i18n from "$/i18n";
import {
	type AdminProfile,
	adminAuthStatusAtom,
	adminProfileAtom,
} from "$/modules/admin-auth/states";
import { globalStore } from "$/states/store";
import { toast } from "$/utils/toast";

const API_BASE_URL = (
	import.meta.env.VITE_API_BASE_URL ?? "https://api.sakanova.f5.si"
).replace(/\/+$/, "");

/** Access Token の期限切れより先回りして refresh するマージン (秒)。 */
const REFRESH_MARGIN_SECONDS = 60;

/**
 * @description 管理 API がエラー応答を返したことを表す例外。
 * `status` に HTTP ステータス、`code` に応答本文の `{ error: "snake_case_code" }`、
 * `retryAfter` に 429/503 の `Retry-After` (秒) を保持する。
 * トークンやパスワードを含む情報は一切持たせない。
 */
export class AdminApiError extends Error {
	readonly status: number;
	readonly code: string | null;
	readonly retryAfter: number | null;

	constructor(
		status: number,
		code: string | null,
		retryAfter: number | null = null,
	) {
		super(code ? `API error ${status} (${code})` : `API error ${status}`);
		this.name = "AdminApiError";
		this.status = status;
		this.code = code;
		this.retryAfter = retryAfter;
	}
}

/** エラー応答から `AdminApiError` を組み立てる。呼び出し側で `throw await ...` する。 */
export async function buildAdminApiError(
	response: Response,
): Promise<AdminApiError> {
	let code: string | null = null;
	try {
		const body = (await response.json()) as unknown;
		if (
			typeof body === "object" &&
			body !== null &&
			"error" in body &&
			typeof (body as { error: unknown }).error === "string"
		) {
			code = (body as { error: string }).error;
		}
	} catch {
		// JSON 以外のエラー本文はコード無しとして扱う。
	}
	return new AdminApiError(
		response.status,
		code,
		parseRetryAfterSeconds(response.headers.get("Retry-After")),
	);
}

function parseRetryAfterSeconds(value: string | null): number | null {
	if (!value) return null;
	const seconds = Number(value);
	if (Number.isFinite(seconds) && seconds >= 0) return Math.ceil(seconds);
	const timestamp = Date.parse(value);
	if (!Number.isFinite(timestamp)) return null;
	return Math.max(1, Math.ceil((timestamp - Date.now()) / 1000));
}

// ---------------------------------------------------------------------------
// Access Token の保持と refresh (メモリのみ・シングルフライト)
// ---------------------------------------------------------------------------

interface TokenState {
	accessToken: string;
	expiresAtMs: number;
}

type RefreshResult = "ok" | "unauthorized" | "failed";

let tokenState: TokenState | null = null;
let refreshTimer: ReturnType<typeof setTimeout> | null = null;
let refreshPromise: Promise<RefreshResult> | null = null;
let initializePromise: Promise<void> | null = null;

interface ParsedTokenResponse {
	accessToken: string;
	expiresIn: number;
	admin: AdminProfile | null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
	return typeof value === "object" && value !== null;
}

function parseAdminProfile(value: unknown): AdminProfile | null {
	if (
		!isRecord(value) ||
		typeof value.uuid !== "string" ||
		typeof value.username !== "string" ||
		typeof value.display_name !== "string" ||
		typeof value.role !== "string"
	) {
		return null;
	}
	return {
		uuid: value.uuid,
		username: value.username,
		display_name: value.display_name,
		role: value.role,
	};
}

function parseTokenResponse(value: unknown): ParsedTokenResponse | null {
	if (
		!isRecord(value) ||
		typeof value.access_token !== "string" ||
		value.access_token === ""
	) {
		return null;
	}
	const expiresIn =
		typeof value.expires_in === "number" &&
		Number.isFinite(value.expires_in) &&
		value.expires_in > 0
			? value.expires_in
			: 900;
	return {
		accessToken: value.access_token,
		expiresIn,
		admin: parseAdminProfile(value.admin),
	};
}

function applyTokens(parsed: ParsedTokenResponse) {
	tokenState = {
		accessToken: parsed.accessToken,
		expiresAtMs: Date.now() + parsed.expiresIn * 1000,
	};
	scheduleProactiveRefresh(parsed.expiresIn);
}

function clearRefreshTimer() {
	if (refreshTimer !== null) {
		clearTimeout(refreshTimer);
		refreshTimer = null;
	}
}

/** `expires_in` から余裕を引いた時点で先回りして refresh する。 */
function scheduleProactiveRefresh(expiresInSeconds: number) {
	clearRefreshTimer();
	const delayMs =
		Math.max(expiresInSeconds - REFRESH_MARGIN_SECONDS, 10) * 1000;
	refreshTimer = setTimeout(() => {
		void refreshAccessToken().then((result) => {
			if (result === "unauthorized") {
				endSession({ notifyExpired: true });
			}
			// "failed" (ネットワーク・5xx) はここでは自動リトライしない。
			// 期限内は現在のトークンで呼び出しを続け、401 時のリトライ経路に委ねる。
		});
	}, delayMs);
}

/**
 * Refresh Token Cookie で新しい Access Token を取得する。
 * ローテーション制のため必ずシングルフライトで実行する。
 */
export function refreshAccessToken(): Promise<RefreshResult> {
	if (refreshPromise) return refreshPromise;
	const promise = performRefresh().finally(() => {
		if (refreshPromise === promise) refreshPromise = null;
	});
	refreshPromise = promise;
	return promise;
}

async function performRefresh(): Promise<RefreshResult> {
	let response: Response;
	try {
		response = await fetch(`${API_BASE_URL}/v1/admin/auth/refresh`, {
			method: "POST",
			credentials: "include",
			cache: "no-store",
			headers: { Accept: "application/json" },
		});
	} catch {
		return "failed";
	}
	if (response.status === 401) return "unauthorized";
	if (!response.ok) return "failed";

	const parsed = parseTokenResponse(
		await response.json().catch(() => undefined),
	);
	if (!parsed) return "failed";

	applyTokens(parsed);
	if (parsed.admin) {
		globalStore.set(adminProfileAtom, parsed.admin);
	}
	return "ok";
}

/**
 * メモリ上のセッションを破棄し、ログイン画面へ遷移させる。
 * `notifyExpired` はセッション切れ (ログアウト操作以外) の場合にトーストで通知する。
 */
function endSession({ notifyExpired }: { notifyExpired: boolean }) {
	clearRefreshTimer();
	tokenState = null;
	const wasAuthenticated =
		globalStore.get(adminAuthStatusAtom) === "authenticated";
	globalStore.set(adminProfileAtom, null);
	globalStore.set(adminAuthStatusAtom, "unauthenticated");
	if (notifyExpired && wasAuthenticated) {
		toast.info(
			i18n.t(
				"adminAuth.sessionExpired",
				"セッションの有効期限が切れました。再度ログインしてください",
			),
		);
	}
}

// ---------------------------------------------------------------------------
// 認証エンドポイント
// ---------------------------------------------------------------------------

/**
 * 管理者ログイン。成功すると Access Token をメモリへ載せ、認証済み状態にする。
 * @throws {AdminApiError} 401 `invalid_credentials`、429 `rate_limited` (Retry-After 付き) など
 */
export async function adminLogin(
	username: string,
	password: string,
): Promise<AdminProfile> {
	const response = await fetch(`${API_BASE_URL}/v1/admin/auth/login`, {
		method: "POST",
		credentials: "include",
		cache: "no-store",
		headers: {
			Accept: "application/json",
			"Content-Type": "application/json",
		},
		body: JSON.stringify({ username, password }),
	});
	if (!response.ok) {
		throw await buildAdminApiError(response);
	}

	const parsed = parseTokenResponse(
		await response.json().catch(() => undefined),
	);
	if (!parsed) {
		throw new AdminApiError(response.status, "invalid_response");
	}

	applyTokens(parsed);
	const admin = parsed.admin ?? (await fetchAdminProfile());
	globalStore.set(adminProfileAtom, admin);
	globalStore.set(adminAuthStatusAtom, "authenticated");
	return admin;
}

/**
 * 管理者ログアウト。API の応答に関わらずメモリ上の Access Token と UI 状態を破棄する。
 */
export async function adminLogout(): Promise<void> {
	const headers = new Headers({ Accept: "application/json" });
	if (tokenState) {
		headers.set("Authorization", `Bearer ${tokenState.accessToken}`);
	}
	try {
		await fetch(`${API_BASE_URL}/v1/admin/auth/logout`, {
			method: "POST",
			credentials: "include",
			cache: "no-store",
			headers,
		});
	} catch {
		// ネットワークエラーでもローカル状態の破棄は行う。
	}
	endSession({ notifyExpired: false });
}

/**
 * 起動時のセッション復元。`POST /v1/admin/auth/refresh` を 1 回だけ試行し、
 * 成功ならログイン済みとして起動、失敗ならログイン画面を表示する。
 * (React StrictMode の二重マウントでも 1 回しか実行されない)
 */
export function initializeAdminSession(): Promise<void> {
	if (initializePromise) return initializePromise;
	initializePromise = (async () => {
		const result = await refreshAccessToken();
		if (result !== "ok") {
			globalStore.set(adminAuthStatusAtom, "unauthenticated");
			return;
		}
		if (!globalStore.get(adminProfileAtom)) {
			try {
				globalStore.set(adminProfileAtom, await fetchAdminProfile());
			} catch {
				// プロフィール取得に失敗しても認証自体は成立しているので続行する。
			}
		}
		globalStore.set(adminAuthStatusAtom, "authenticated");
	})();
	return initializePromise;
}

/** `GET /v1/admin/me` でログイン中のプロフィールを取得する。 */
async function fetchAdminProfile(): Promise<AdminProfile> {
	const response = await adminFetch("/v1/admin/me");
	if (!response.ok) {
		throw await buildAdminApiError(response);
	}
	const body = (await response.json().catch(() => undefined)) as unknown;
	const admin = isRecord(body) ? parseAdminProfile(body.admin) : null;
	if (!admin) {
		throw new AdminApiError(response.status, "invalid_response");
	}
	return admin;
}

// ---------------------------------------------------------------------------
// 認証付き fetch (管理 API 呼び出しはすべてここを通す)
// ---------------------------------------------------------------------------

async function ensureAccessToken(): Promise<string> {
	// 期限まで十分な余裕があればそのまま使う (期限管理は expires_in 起点)。
	if (tokenState && tokenState.expiresAtMs - Date.now() > 5_000) {
		return tokenState.accessToken;
	}
	const result = await refreshAccessToken();
	const current = tokenState;
	if (result !== "ok" || !current) {
		endSession({ notifyExpired: result === "unauthorized" });
		throw new AdminApiError(401, "unauthorized");
	}
	return current.accessToken;
}

function requestWithBearer(
	path: string,
	init: RequestInit,
	accessToken: string,
): Promise<Response> {
	const headers = new Headers(init.headers);
	headers.set("Authorization", `Bearer ${accessToken}`);
	if (!headers.has("Accept")) {
		headers.set("Accept", "application/json");
	}
	// 管理 API (auth 系以外) は Cookie 不要。Bearer のみで認証する。
	return fetch(`${API_BASE_URL}${path}`, {
		...init,
		headers,
		credentials: "omit",
	});
}

/**
 * 管理 API 以外の sakanova 系サービス (特急 API など) が `Authorization: Bearer` を
 * 組み立てるための Access Token 取得。期限切れが近い場合は refresh してから返す。
 * 取得できない場合はセッションを破棄して `AdminApiError(401)` を送出する。
 */
export function getAdminAccessToken(): Promise<string> {
	return ensureAccessToken();
}

/**
 * 管理 API 用 fetch。Access Token を自動付与し、401 なら refresh 後に
 * **元リクエストを 1 回だけ再試行**する。再試行後も 401、または refresh 自体が
 * 失敗した場合はセッションを破棄してログイン画面へ遷移させる。
 *
 * 注意: `init.body` は再試行で再送されるため、文字列など再利用可能な値のみ渡すこと。
 */
export async function adminFetch(
	path: string,
	init: RequestInit = {},
): Promise<Response> {
	const accessToken = await ensureAccessToken();
	const response = await requestWithBearer(path, init, accessToken);
	if (response.status !== 401) return response;

	const result = await refreshAccessToken();
	const current = tokenState;
	if (result !== "ok" || !current) {
		endSession({ notifyExpired: true });
		throw await buildAdminApiError(response);
	}

	const retried = await requestWithBearer(path, init, current.accessToken);
	if (retried.status === 401) {
		endSession({ notifyExpired: true });
	}
	return retried;
}
